/* Universal Basic — WebGL sun, close-up solar-surface style (three.js r128 + Unreal bloom).
   Big curved limb on the left, churning photoreal plasma filling the frame.
   Loads self-hosted three.js (window.UB_SUN_SRC) with a CDN fallback; lazy-inits on scroll. */
(function () {
  'use strict';
  if (window.__ubSunInit) return;
  window.__ubSunInit = true;

  var CDN = 'https://cdn.jsdelivr.net/npm/three@0.128.0';
  var FALLBACK = [
    CDN + '/build/three.min.js',
    CDN + '/examples/js/shaders/CopyShader.js',
    CDN + '/examples/js/shaders/LuminosityHighPassShader.js',
    CDN + '/examples/js/postprocessing/EffectComposer.js',
    CDN + '/examples/js/postprocessing/ShaderPass.js',
    CDN + '/examples/js/postprocessing/RenderPass.js',
    CDN + '/examples/js/postprocessing/UnrealBloomPass.js'
  ];
  var SCRIPTS = (window.UB_SUN_SRC && window.UB_SUN_SRC.length === 7) ? window.UB_SUN_SRC : FALLBACK;
  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var timeScale = reduceMotion ? 0.3 : 1.0;

  function hasWebGL() {
    try { var c = document.createElement('canvas'); return !!(window.WebGLRenderingContext && (c.getContext('webgl') || c.getContext('experimental-webgl'))); }
    catch (e) { return false; }
  }
  function loadSequential(urls, done) {
    var i = 0;
    (function next() {
      if (i >= urls.length) return done(true);
      var s = document.createElement('script'); s.src = urls[i++]; s.async = false;
      s.onload = next; s.onerror = function () { done(false); };
      document.head.appendChild(s);
    })();
  }

  var NOISE = [
    'vec4 permute(vec4 x){return mod(((x*34.0)+1.0)*x,289.0);}',
    'vec4 taylorInvSqrt(vec4 r){return 1.79284291400159-0.85373472095314*r;}',
    'float snoise(vec3 v){',
    '  const vec2 C=vec2(1.0/6.0,1.0/3.0); const vec4 D=vec4(0.0,0.5,1.0,2.0);',
    '  vec3 i=floor(v+dot(v,C.yyy)); vec3 x0=v-i+dot(i,C.xxx);',
    '  vec3 g=step(x0.yzx,x0.xyz); vec3 l=1.0-g; vec3 i1=min(g.xyz,l.zxy); vec3 i2=max(g.xyz,l.zxy);',
    '  vec3 x1=x0-i1+C.xxx; vec3 x2=x0-i2+2.0*C.xxx; vec3 x3=x0-1.0+3.0*C.xxx;',
    '  i=mod(i,289.0);',
    '  vec4 p=permute(permute(permute(i.z+vec4(0.0,i1.z,i2.z,1.0))+i.y+vec4(0.0,i1.y,i2.y,1.0))+i.x+vec4(0.0,i1.x,i2.x,1.0));',
    '  float n_=1.0/7.0; vec3 ns=n_*D.wyz-D.xzx;',
    '  vec4 j=p-49.0*floor(p*ns.z*ns.z);',
    '  vec4 x_=floor(j*ns.z); vec4 y_=floor(j-7.0*x_);',
    '  vec4 x=x_*ns.x+ns.yyyy; vec4 y=y_*ns.x+ns.yyyy; vec4 h=1.0-abs(x)-abs(y);',
    '  vec4 b0=vec4(x.xy,y.xy); vec4 b1=vec4(x.zw,y.zw);',
    '  vec4 s0=floor(b0)*2.0+1.0; vec4 s1=floor(b1)*2.0+1.0; vec4 sh=-step(h,vec4(0.0));',
    '  vec4 a0=b0.xzyw+s0.xzyw*sh.xxyy; vec4 a1=b1.xzyw+s1.xzyw*sh.zzww;',
    '  vec3 p0=vec3(a0.xy,h.x); vec3 p1=vec3(a0.zw,h.y); vec3 p2=vec3(a1.xy,h.z); vec3 p3=vec3(a1.zw,h.w);',
    '  vec4 norm=taylorInvSqrt(vec4(dot(p0,p0),dot(p1,p1),dot(p2,p2),dot(p3,p3)));',
    '  p0*=norm.x; p1*=norm.y; p2*=norm.z; p3*=norm.w;',
    '  vec4 m=max(0.6-vec4(dot(x0,x0),dot(x1,x1),dot(x2,x2),dot(x3,x3)),0.0); m=m*m;',
    '  return 42.0*dot(m*m,vec4(dot(p0,x0),dot(p1,x1),dot(p2,x2),dot(p3,x3)));',
    '}',
    'float fbm(vec3 p){ float v=0.0,a=0.5; for(int i=0;i<7;i++){ v+=a*snoise(p); p*=2.05; a*=0.5;} return v; }'
  ].join('\n');

  var SUN_VERT = 'varying vec3 vPos; varying vec3 vN; varying vec3 vV; void main(){ vPos=position; vec4 mv=modelViewMatrix*vec4(position,1.0); vV=-mv.xyz; vN=normalMatrix*normal; gl_Position=projectionMatrix*mv; }';

  var SUN_FRAG = [
    'precision highp float;', 'uniform float uTime;',
    'varying vec3 vPos; varying vec3 vN; varying vec3 vV;', NOISE,
    'void main(){',
    '  vec3 dir=normalize(vPos);',
    '  vec3 sp=dir*4.6; float t=uTime*0.16;',
    // multi-octave domain warp -> swirling plasma
    '  vec3 w1=vec3(fbm(sp+vec3(0.0,0.0,t)),fbm(sp+vec3(2.7,1.1,-t)),fbm(sp+vec3(5.3,8.1,t*0.7)));',
    '  vec3 w2=vec3(fbm(sp*2.0+w1*2.0+t),fbm(sp*2.0+w1*2.0+vec3(3.1)),fbm(sp*2.0+w1*2.0-t*0.6));',
    '  float plasma=fbm(sp*1.5+w1*2.6+w2*1.2+vec3(t*0.5)); float pn=plasma*0.5+0.5;',
    '  float gran=fbm(sp*9.5+w1*1.6-t*0.9)*0.5+0.5;',          // fine granulation
    '  float fil=pow(1.0-abs(fbm(sp*3.2+w1*1.2)),3.0);',        // dark filaments / threads
    '  float h=pn*0.7+gran*0.3; h=pow(clamp(h,0.0,1.0),1.55);', // contrast
    '  vec3 cBlack=vec3(0.015,0.0,0.0), cDeep=vec3(0.38,0.04,0.0), cOrange=vec3(1.0,0.36,0.03), cYellow=vec3(1.0,0.76,0.28), cWhite=vec3(1.0,0.95,0.84);',
    '  vec3 col=mix(cBlack,cDeep,smoothstep(0.0,0.22,h));',
    '  col=mix(col,cOrange,smoothstep(0.2,0.48,h));',
    '  col=mix(col,cYellow,smoothstep(0.46,0.76,h));',
    '  col=mix(col,cWhite,smoothstep(0.76,1.0,h));',
    '  col*=(1.0-fil*0.55);',                                   // dark filament threads
    '  col+=cWhite*pow(max(h-0.72,0.0)/0.28,2.0)*1.6;',         // blazing active regions -> bloom
    '  float facing=max(dot(normalize(vN),normalize(vV)),0.0);',
    '  col*=mix(0.55,1.0,pow(facing,0.45));',                   // gentle limb darkening
    '  col+=vec3(1.0,0.5,0.16)*pow(1.0-facing,3.0)*1.6;',       // bright chromosphere limb
    '  col*=1.5;',
    '  gl_FragColor=vec4(col,1.0);',
    '}'
  ].join('\n');

  function initSun(host) {
    function dims() { var r = host.getBoundingClientRect(); return { w: Math.max(2, Math.round(r.width)), h: Math.max(2, Math.round(r.height)) }; }
    var d = dims(), w = d.w, h = d.h;

    var scene = new THREE.Scene();
    scene.background = new THREE.Color(0x000000);

    var camera = new THREE.PerspectiveCamera(45, w / h, 0.1, 100);
    camera.position.set(0, 0, 5);
    camera.lookAt(0, 0, 0);

    var renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setSize(w, h);
    renderer.domElement.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;';
    host.appendChild(renderer.domElement);

    var uniforms = { uTime: { value: 0 } };
    var sun = new THREE.Mesh(
      new THREE.IcosahedronGeometry(3.0, 7),
      new THREE.ShaderMaterial({ uniforms: uniforms, vertexShader: SUN_VERT, fragmentShader: SUN_FRAG })
    );
    sun.position.set(1.95, 0.05, 0);  // shift right -> curved limb sits on the left
    sun.rotation.z = 0.25;
    scene.add(sun);

    var composer = new THREE.EffectComposer(renderer);
    composer.addPass(new THREE.RenderPass(scene, camera));
    var bloom = new THREE.UnrealBloomPass(new THREE.Vector2(w, h), 1.15, 0.55, 0.55);
    composer.addPass(bloom);

    function resize() {
      var dd = dims();
      renderer.setSize(dd.w, dd.h); composer.setSize(dd.w, dd.h);
      camera.aspect = dd.w / dd.h; camera.updateProjectionMatrix();
    }
    if (window.ResizeObserver) new ResizeObserver(resize).observe(host); else window.addEventListener('resize', resize);

    var running = true, last = performance.now();
    new IntersectionObserver(function (es) { running = es[0].isIntersecting; if (running) { last = performance.now(); loop(); } }, { threshold: 0.01 }).observe(host);

    function loop() {
      if (!running) return;
      var now = performance.now(), dt = Math.min((now - last) / 1000, 0.05); last = now;
      uniforms.uTime.value += dt * timeScale;
      sun.rotation.y += dt * 0.018 * timeScale;
      composer.render();
      requestAnimationFrame(loop);
    }
    loop();
    var vis = host.closest('.ub-fs__visual'); if (vis) vis.classList.add('ub-has-gl');
  }

  function boot() {
    var hosts = [].slice.call(document.querySelectorAll('[data-ub-sun]'));
    if (!hosts.length || !hasWebGL()) return;
    var io = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        obs.unobserve(e.target);
        var host = e.target;
        if (window.THREE && window.THREE.EffectComposer) { try { initSun(host); } catch (err) { console.warn('[ub-sun]', err); } return; }
        loadSequential(SCRIPTS, function (ok) {
          if (ok && window.THREE && window.THREE.EffectComposer) { try { initSun(host); } catch (err) { console.warn('[ub-sun]', err); } }
          else { console.warn('[ub-sun] three.js failed to load'); }
        });
      });
    }, { rootMargin: '300px' });
    hosts.forEach(function (h) { io.observe(h); });
  }

  if (document.readyState !== 'loading') boot();
  else document.addEventListener('DOMContentLoaded', boot);
})();
